/**
 * ReferencedListUI
 *
 * Renders the entity record's top-level `referencedList` property — the reverse index of
 * entity references maintained by the backend `docMirror` trigger — as a read-only table,
 * plus the one action that can add to it: creating a record of a declared source type that
 * already points at this record. Consumer: the `referencedList` layout element, dispatched
 * by `LayoutUI`.
 *
 * Reads the value off `useEntityRecordScope()`, whose `entityRecord.data` comes straight
 * from the tracking proxy, so the `.referencedList` read below narrows the subscription.
 * `useCurrentArtifact` is deliberately avoided: it calls `useFormStore()` unconditionally
 * and `FormVersion` carries no `referencedList`.
 *
 * Invariants: the list itself has no write path — a new row is never inserted optimistically,
 * it arrives from the trigger on the live subscription, which is why the create action opens
 * the new record in the entity sidebar instead of navigating away from this one. The value is
 * a MAP of encoded source path to source name; the key is the only stored coordinate, so the
 * link target and the entity id are decoded from it rather than read off the entry. The
 * offered source types are exactly the declared (source → target) pairs of
 * `parameters/referencedList` that resolve to a live entity; with none offerable the action is
 * disabled, never hidden. The create action carries every lock the host imposes (`disabled`),
 * the record's own `entityLocked`, and the `recordsWrite` right the rule enforces, so it is
 * never clickable into a permission denial. The seeded reference is keyed the way the SOURCE
 * entity's own layout declares it — `entityGroupId || entityId`, recovered through
 * `entityIdByReferenceKey` — so the new record's reference field shows the link instead of
 * offering to write a second, duplicate one. The table is static, so PDF export renders it
 * as-is minus the create control — a print surface has no actions.
 *
 * The table fills its parent and is capped at `MAX_LIMITED_WIDTH_VIEWPORT_NARROW`, matching
 * every other reading surface in the app. Only `Type` is sized; `Document` takes the rest.
 */
import { FC, useContext, useState } from 'react';
import { Button, Dropdown, Typography } from 'antd';
import ResponsiveTable from '@/components/atoms/ResponsiveTable';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';

import {
  DEFAULT_NEW_RECORD_NAME,
  MAX_LIMITED_WIDTH_VIEWPORT_NARROW,
  decodeReferencedListKey,
  parseReferencedListRelations,
  type EntityId,
  type UIReferencedList,
} from '#types';
import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';
import FieldLabel from '@/components/atoms/FieldLabel';
import Icon from '@/components/atoms/Icon';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import { EntitiesContext } from '@/providers/artifacts/EntitiesContext';
import { MessageContext } from '@/providers/MessageContext';
import { SidebarControlContext } from '@/providers/SidebarControlContext';
import { UserContext } from '@/providers/UserContext';
import { entityRecordActions, useEntityRecordScope } from '@/store';
import { entityIdByReferenceKey } from '@/use/entityIdByReferenceKey';
import { useReferencedListConfig } from '@/use/useReferencedListConfig';

const { Text } = Typography;

type ReferencedListRow = {
  key: string;
  path: string;
  entityId: string;
  name: string;
};

const referencedListColumns: ColumnsType<ReferencedListRow> = [
  {
    dataIndex: 'name',
    title: 'Document',
    render: (_value, row) => (
      <Link
        to={`/${row.path}`}
        aria-label={`referenced-list-open-${row.name || row.path}`}
      >
        {row.name || row.path}
      </Link>
    ),
  },
  {
    dataIndex: 'entityId',
    title: 'Type',
    width: 160,
    render: (_value, row) => <Text>{row.entityId}</Text>,
  },
];

type ReferencedListUIProps = {
  uiReferencedList: UIReferencedList;
  /** Read-only mode imposed by the host (locked record, protected form, PDF portal). */
  disabled?: boolean;
};

const ReferencedListUI: FC<ReferencedListUIProps> = ({ uiReferencedList, disabled = false }) => {
  const scope = useEntityRecordScope();
  const { config, loading } = useReferencedListConfig();
  const { entities, isLoading: entitiesLoading } = useContext(EntitiesContext);
  const sidebarControl = useContext(SidebarControlContext);
  const { user, userRights } = useContext(UserContext);
  const { handleFirestoreWriteError } = useContext(MessageContext);
  const { isPdfExportMode } = usePdfExportContext();
  const [creating, setCreating] = useState(false);

  const sources = [...parseReferencedListRelations(config)]
    .filter(([sourceEntityId, targets]) => targets.has(scope.entityId) && !!entities[sourceEntityId])
    .map(([sourceEntityId]) => ({
      entityId: sourceEntityId,
      label: entities[sourceEntityId]?.description?.shortLabel || sourceEntityId,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const canCreate =
    !disabled &&
    !scope.entityLocked &&
    !!userRights['recordsWrite'] &&
    !loading &&
    !entitiesLoading &&
    !creating &&
    sources.length > 0 &&
    !!scope.recordId &&
    !!sidebarControl;

  const createReferencingRecord = async (sourceEntityId: EntityId): Promise<void> => {
    const control = sidebarControl;
    const targetEntityId = scope.entityId;
    const targetRecordId = scope.recordId;
    const targetRecordName = scope.entityRecord.data?.name;
    const userId = user?.uid;
    if (!control || !targetRecordId) return;

    const declaredKeys = entityIdByReferenceKey(
      entities[sourceEntityId]?.defaultUILayout?.structure,
    );
    const referenceKey = Object.keys(declaredKeys).find(
      (key) => declaredKeys[key] === targetEntityId,
    );

    setCreating(true);
    try {
      const newRecordId = await entityRecordActions.createEntityRecord(
        sourceEntityId,
        userId,
        undefined,
        undefined,
        {
          entityId: targetEntityId,
          recordId: targetRecordId,
          recordName: targetRecordName,
          entityGroupId: referenceKey === targetEntityId ? undefined : referenceKey,
        },
      );
      control.openEntitySidebar(
        sourceEntityId,
        newRecordId,
        undefined,
        DEFAULT_NEW_RECORD_NAME,
        undefined,
        true,
      );
    } catch (error) {
      handleFirestoreWriteError(error, '[ReferencedListUI] createEntityRecord');
    } finally {
      setCreating(false);
    }
  };

  if (!scope.insideEntityRecordContext) {
    return null;
  }

  const rows: ReferencedListRow[] = Object.entries(
    scope.entityRecord.data?.referencedList ?? {},
  )
    .filter(([, name]) => typeof name === 'string')
    .map(([key, name]) => {
      const path = decodeReferencedListKey(key);
      return { key, path, entityId: path.split('/')[1] ?? '', name };
    })
    .sort((a, b) => (a.name || a.path).localeCompare(b.name || b.path));

  const singleSource = sources.length === 1 ? sources[0] : undefined;
  const createButton = (
    <Button
      type="link"
      size="small"
      icon={<Icon.Plus />}
      disabled={!canCreate}
      loading={creating}
      onClick={singleSource ? () => void createReferencingRecord(singleSource.entityId) : undefined}
      aria-label="referenced-list-create-record"
    >
      New {singleSource ? singleSource.label : 'referencing record'}
    </Button>
  );

  return (
    <FlexCol
      gap={0}
      {...uiReferencedList.props}
      style={{
        maxWidth: MAX_LIMITED_WIDTH_VIEWPORT_NARROW,
        ...uiReferencedList.props?.style,
      }}
      id="referenced-list-ui-container"
      data-testid="referenced-list-ui"
      aria-label="Referenced by section"
    >
      <FlexBox noGrow stretch>
        <FieldLabel
          label="Referenced By"
          required={uiReferencedList.required}
          tooltip="Documents that reference this record"
        />
        {!isPdfExportMode &&
          (singleSource ? (
            createButton
          ) : (
            <Dropdown
              trigger={['click']}
              disabled={!canCreate}
              menu={{
                items: sources.map((source) => ({
                  key: source.entityId,
                  label: source.label,
                  onClick: () => void createReferencingRecord(source.entityId),
                })),
              }}
            >
              {createButton}
            </Dropdown>
          ))}
      </FlexBox>
      {rows.length === 0 ? (
        <Text type="secondary" aria-label="referenced-list-empty">
          No documents reference this record
        </Text>
      ) : (
        <ResponsiveTable<ReferencedListRow>
          style={{ width: '100%' }}
          aria-label="referenced-list-table"
          columns={referencedListColumns}
          dataSource={rows}
          pagination={false}
          size="small"
        />
      )}
    </FlexCol>
  );
};

export default ReferencedListUI;
